import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AssessmentActivityRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetAssessmentActivitiesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC assessmentActivity data to match the GraphQL query structure
 */
const mapTrpcAssessmentActivitiesToGraphQL = (
  trpcData: AssessmentActivityRegisterResponse
): GetAssessmentActivitiesQuery => {
  return {
    assessment_activity: trpcData.assessment_activity,
  };
};

export const useGetAssessmentActivitiesRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.activityRegister.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcAssessmentActivitiesToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
